/**
 * Why watchlist stocks moved (issue #317) — ONE definition of how the mover-news payload
 * (`useMoverNews`: `{ session_date, as_of, stale, themes[], movers{} }`) is read, shared by the
 * expanded stock row (`MoverExplanation`) and the mobile Brief's movers cards (`App.js`). Two
 * surfaces hand-reading the same payload is how they come to disagree.
 */

export const NO_NEWS_TEXT = 'No specific news found for this move.';

export const fmtMovePct = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? `${value > 0 ? '+' : ''}${value.toFixed(1)}%` : '';

export const themeOf = (data, ticker) => {
  const mover = data?.movers?.[ticker];
  if (!mover?.themeId) return null;
  return (data.themes || []).find((t) => t.id === mover.themeId) || null;
};

/**
 * The explanation to show for one ticker: its theme's prose when it belongs to one (the theme
 * carries the text; the row only points at it), else its own summary, else the no-news line.
 * `null` when the ticker was not a mover this session.
 */
export const explanationFor = (data, ticker) => {
  const mover = data?.movers?.[ticker];
  if (!mover) return null;
  const base = { ticker, mover, sessionDate: data.session_date || null };
  const theme = themeOf(data, ticker);
  if (theme) return { ...base, text: theme.summary, sources: theme.sources || [], theme, noNews: false };
  if (mover.noNews || !mover.summary) return { ...base, text: NO_NEWS_TEXT, sources: [], theme: null, noNews: true };
  return { ...base, text: mover.summary, sources: mover.sources || [], theme: null, noNews: false };
};

export const sourceHost = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * Tooltip line for the row marker (issue #324): the move, then the theme title or the summary,
 * cut at a word so the hover stays a glance. A no-news explanation gets no marker at all.
 */
export const indicatorText = (explanation, max = 180) => {
  if (!explanation || explanation.noNews) return null;
  const gist = explanation.theme ? `${explanation.theme.title}: ${explanation.text}` : explanation.text;
  const head = gist.length > max ? `${gist.slice(0, max).replace(/\s+\S*$/, '')}…` : gist;
  return `Why it moved ${fmtMovePct(explanation.mover.changePercent)} · ${head}`;
};
